import {
  ButtonTypes,
  CreationRenderTypeEnum,
  FormEngineModeTypeEnum,
} from '@celito.clients/enums';
import { useLayout } from '@celito.clients/hooks';
import { View } from '@celito.clients/list-view-engine';
import { useRecordNavigation } from '@celito.clients/provider';
import {
  CustomButton,
  Icon,
  IconButton,
  useBreadcrumbs,
} from '@celito.clients/shared';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
} from '@fluentui/react-components';
import { useFocusWithin } from '@fluentui/react-tabster';
import { useListViewContext } from 'libs/list-view-engine/src/lib/hooks';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import FormEngine from '../../form-engine';
import { FormTypeRendererStyles } from './form-type-renderer.styles';

interface FormRendererProps {
  parentObjectName?: string;
  viewData?: View;
  route: string;
  buttonTitle: string;
  buttonType?: ButtonTypes;
  mode?: FormEngineModeTypeEnum;
}

export const FormTypeRenderer = ({
  parentObjectName,
  viewData,
  route,
  buttonTitle,
  buttonType = ButtonTypes.Primary,
  mode = FormEngineModeTypeEnum.Create,
}: FormRendererProps) => {
  const classes = FormTypeRendererStyles();
  const { addBreadcrumb } = useBreadcrumbs();
  const [searchParams, setSearchParams] = useSearchParams();
  const parentRecord = searchParams.get('pr');

  const { previousNavItem } = useRecordNavigation();
  const { recordName } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const { headerTitle } = useLayout();
  const {
    fetchRowData,
    showShimmeringRows,
    currentPageNumber,
    filters,
    sortConfig,
  } = useListViewContext();

  const viewType =
    viewData?.creationOfNewRecordRenderType ?? CreationRenderTypeEnum.Page;
  const viewName = viewData?.createRecordViewName ?? '';

  // Explicit methods for clarity
  const openDialog = () => setIsOpen(true);
  const closeDialog = () => setIsOpen(false);

  // Use headerTitle as a stable key for both dialog and drawer
  const focusWithinAttributes = useFocusWithin();

  const updateSearchParams = () => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set('pr', recordName ?? '');
    updatedParams.set('po', parentObjectName ?? '');
    setSearchParams(updatedParams, { replace: true });
  };

  let urlWithBreadcrumb = addBreadcrumb({
    label: buttonTitle,
    path: route,
  });

  if (previousNavItem?.isRelatedObject) {
    urlWithBreadcrumb = `${urlWithBreadcrumb}&pr=${recordName}&po=${parentObjectName}`;
  }

  const removeParentRecordSearchParam = () => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete('po');
    updatedParams.delete('pr');
    setSearchParams(updatedParams, { replace: true });
  };

  const handleOnClose = () => {
    closeDialog();

    if (!parentRecord) {
      showShimmeringRows();
      fetchRowData(
        viewData?.objectName as string,
        currentPageNumber,
        filters,
        sortConfig
      );
    } else {
      removeParentRecordSearchParam();
    }
  };

  const handleOnSubmit = () => {
    handleOnClose();
  };

  // Robust focus restoration: always restore focus to dialog if open and focus leaves it
  useEffect(() => {
    function onFocusIn(event: FocusEvent) {
      if (!isOpen || !dialogContentRef.current) return;
      if (!dialogContentRef.current.contains(event.target as Node)) {
        const focusable = dialogContentRef.current.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }
    }
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [isOpen]);

  switch (viewType) {
    case 'modal':
      return (
        <Dialog
          open={isOpen}
          key={viewName}
          {...focusWithinAttributes}
          inertTrapFocus
        >
          <DialogTrigger disableButtonEnhancement>
            <CustomButton
              buttonTitle={buttonTitle}
              buttonType={buttonType}
              onClick={() => {
                if (previousNavItem?.isRelatedObject) {
                  updateSearchParams();
                }
                isOpen ? closeDialog() : openDialog();
              }}
            />
          </DialogTrigger>
          <DialogSurface
            className={classes.dialogSurface}
            style={{
              opacity: isOpen ? 1 : 0,
              transition: 'opacity 100ms ease-in-out',
            }}
          >
            <DialogBody style={{ height: '100%', gap: 0 }}>
              <DialogTitle className={classes.dialogTitle}>
                {headerTitle}
                <IconButton
                  appearance="subtle"
                  icon={<Icon iconName="Dismiss24Regular" />}
                  onClick={handleOnClose}
                />
              </DialogTitle>
              <DialogContent ref={dialogContentRef}>
                <FormEngine
                  viewName={viewName}
                  mode={mode}
                  onCancel={handleOnClose}
                  onSubmit={handleOnSubmit}
                />
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      );
    case 'panel':
      return (
        <div>
          <Drawer
            key={`drawer-${headerTitle}`}
            type="overlay"
            open={isOpen}
            size="large"
            position="end"
            {...focusWithinAttributes}
          >
            <DrawerHeader style={{ padding: 0 }}>
              <DrawerHeaderTitle
                className={classes.dialogTitle}
                action={
                  <IconButton
                    appearance="subtle"
                    icon={<Icon iconName="Dismiss24Regular" />}
                    onClick={handleOnClose}
                  />
                }
              >
                {headerTitle}
              </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody style={{ padding: 0 }}>
              <FormEngine
                viewName={viewName}
                mode={mode}
                onCancel={() => handleOnClose}
                onSubmit={() => handleOnSubmit}
              />
            </DrawerBody>
          </Drawer>
          <CustomButton
            buttonTitle={buttonTitle}
            buttonType={buttonType}
            onClick={() => {
              if (previousNavItem?.isRelatedObject) {
                updateSearchParams();
              }
              isOpen ? closeDialog() : openDialog();
            }}
          />
        </div>
      );

    default:
      return (
        <Link to={urlWithBreadcrumb} style={{ textDecoration: 'none' }}>
          <CustomButton buttonTitle={buttonTitle} buttonType={buttonType} />
        </Link>
      );
  }
};
